/**
 * Writes a mail merge data source (Serienbrief-Datenquelle) the way the Word templates expect it: an RTF
 * document holding one table, whose first row names the merge fields and whose every further row is one
 * recipient. The legacy produced these files as Access RTF exports with a `.doc` extension, and the
 * templates still open them under those names. The table is laid out like those exports — bordered, with
 * a shaded header — so a file opened to check what goes out looks as it always did; the merge itself reads
 * only the cell contents.
 *
 * Every character outside ASCII is written as an RTF Unicode escape rather than as a Windows-1252 byte.
 * Word reads both the same way, but Windows-1252 has no `č`, `ć` or `ı` — common in the names of the
 * people these letters go to — and would turn them into `?`. It also leaves the output pure ASCII, so no
 * code page has to be encoded in the browser.
 */

/** One merge field of the data source and how its column is laid out. */
export interface RtfDataSourceColumn {
  name: string;
  /** In twips (1/1440 inch). */
  width: number;
  alignRight?: boolean;
}

// Indices into the colour table below, in the order it lists them: the colours of the legacy exports.
const COLOR_BLACK = 0;
const COLOR_SILVER = 1;
const COLOR_BLUE_GREY = 2;

const RTF_HEADER =
  "{\\rtf1\\ansi\\ansicpg1252\\deff0{\\fonttbl{\\f0\\fnil\\fcharset0 Calibri;}}" +
  "{\\colortbl\\red0\\green0\\blue0;\\red192\\green192\\blue192;\\red208\\green215\\blue229;}\\fs22";

// Half the space between two cells' text, in twips.
const CELL_GAP = 40;
// Shading is given in hundredths of a percent; the header is filled completely.
const FULL_SHADING = 10000;

// A signed 16-bit value is what the RTF \u control word takes, so code units above it are written negative.
const RTF_UNICODE_WRAP = 0x10000;
const RTF_UNICODE_MAX = 0x7fff;
const ASCII_PRINTABLE_FIRST = 0x20;
const ASCII_PRINTABLE_LAST = 0x7e;

/** Builds the data source; the result is ASCII and can be written as it is. */
export function buildRtfDataSource(
  columns: readonly RtfDataSourceColumn[],
  rows: ReadonlyArray<readonly string[]>
): Uint8Array<ArrayBuffer> {
  const edges = columnEdges(columns);

  const headerRow = tableRow(
    rowLayout(edges, COLOR_BLACK, COLOR_SILVER),
    columns.map((column) => `\\qc {\\b ${escapeRtf(column.name)}}`)
  );
  const recipientLayout = rowLayout(edges, COLOR_BLUE_GREY);
  const recipientRows = rows.map((values) =>
    tableRow(
      recipientLayout,
      columns.map((column, index) => `${column.alignRight ? "\\qr" : ""} ${escapeRtf(values[index] ?? "")}`)
    )
  );

  return new TextEncoder().encode(`${RTF_HEADER}${headerRow}${recipientRows.join("")}}`);
}

/** RTF positions a cell by where it ends, so each edge is the sum of the widths up to that column. */
function columnEdges(columns: readonly RtfDataSourceColumn[]): number[] {
  let edge = 0;
  return columns.map((column) => (edge += column.width));
}

function rowLayout(edges: readonly number[], borderColor: number, shadingColor?: number): string {
  const border = (side: string) => `\\clbrdr${side}\\brdrs\\brdrcf${borderColor}`;
  const shading = shadingColor === undefined ? "" : `\\clshdng${FULL_SHADING}\\clcfpat${shadingColor}`;
  const cell = `${border("l")}${border("t")}${border("b")}${border("r")}${shading}`;

  return `\\trowd\\trgaph${CELL_GAP}${edges.map((edge) => `${cell}\\cellx${edge}`).join("")}`;
}

function tableRow(layout: string, cells: readonly string[]): string {
  return `${layout}${cells.map((cell) => `\\pard\\intbl${cell}\\cell`).join("")}\\row`;
}

/**
 * A raw line break inside an RTF cell is ignored by every reader, so breaks become \line. The space after
 * a control word only ends it and is not part of the text.
 */
function escapeRtf(value: string): string {
  return Array.from(value.replace(/\r\n?/g, "\n"), escapeRtfChar).join("");
}

function escapeRtfChar(char: string): string {
  switch (char) {
    case "\\":
      return "\\\\";
    case "{":
      return "\\{";
    case "}":
      return "\\}";
    case "\n":
      return "\\line ";
    case "\t":
      return "\\tab ";
  }

  const codePoint = char.codePointAt(0) ?? 0;

  if (codePoint >= ASCII_PRINTABLE_FIRST && codePoint <= ASCII_PRINTABLE_LAST) {
    return char;
  }

  if (codePoint < ASCII_PRINTABLE_FIRST) {
    return "";
  }

  // Iterating a string yields whole code points, but \u takes UTF-16 code units — a character outside the
  // Basic Multilingual Plane is written as its surrogate pair. The "?" is what a reader without Unicode
  // support shows instead.
  return Array.from({ length: char.length }, (_, index) => {
    const unit = char.charCodeAt(index);
    return `\\u${unit > RTF_UNICODE_MAX ? unit - RTF_UNICODE_WRAP : unit}?`;
  }).join("");
}
